import { AppRegion } from './appRegion';

/**
 * @description Contains configuration data representing a different instance of this
 * app in a different region. This is to facilitate redirecting users to more regionally
 * specific instances if needed.
 *
 * All RegionalConfigs need a corresponding feature flag key; this allows us to pre-configure
 * regions before enabling them for users. The requirements for a region to be "available" are:
 * 1) The regional config is fully configured for the region
 * 2) The corresponding feature flag for that region is enabled for the current user
 */
interface RegionalConfig {
  /**
   * @description The URL to redirect users to for the dashboard in another region.
   * @type string
   */
  dashboardUrl: string;

  /**
   * @description The translation key for the name of this region (so we can internationalize
   * the regionalized configuration~~~)
   * @type string
   */
  labelTranslationKey: string;

  /**
   * @description The default language to set for the given region (as a locale code e.g. 'en-US')
   * @type string
   */
  languageKey: string;

  // @TODO need verbiage check in PR: technically we're "regional" but product
  /**
   * @description The country code (unique per config) for the given regional config
   * @type string
   */
  countryCode: string;

  /**
   * @description The key for the region's feature flag descriptor. If this is not enabled,
   * the app should not advertise the given region
   */
  featureFlagKey: string;
}

// Order matters in this, we'll expose the entries in the UI in this specific order
// (because there's no ordinal scheme given by product). The `index` of the array that
// a config falls in is its ordinal numbering.
const regionBaseConfig: RegionalConfig[] = [
  {
    labelTranslationKey: 'index:regionSelector.us.label',
    featureFlagKey: 'us',
    languageKey: 'en-US',
    countryCode: 'US',
    dashboardUrl: process.env.US_DASHBOARD_URL!,
  },
  {
    labelTranslationKey: 'index:regionSelector.se.label',
    featureFlagKey: 'se',
    languageKey: 'sv-SE',
    countryCode: 'SE',
    dashboardUrl: process.env.SE_DASHBOARD_URL!,
  },
  {
    labelTranslationKey: 'index:regionSelector.au.label',
    featureFlagKey: 'au',
    languageKey: 'en-AU',
    countryCode: 'AU',
    dashboardUrl: process.env.AU_DASHBOARD_URL!,
  },
  {
    labelTranslationKey: 'index:regionSelector.de.label',
    featureFlagKey: 'de',
    languageKey: 'de-DE',
    countryCode: 'DE',
    dashboardUrl: process.env.DE_DASHBOARD_URL!,
  },
  {
    labelTranslationKey: 'index:regionSelector.uk.label',
    featureFlagKey: 'uk',
    languageKey: 'en-GB',
    countryCode: 'UK',
    dashboardUrl: process.env.UK_DASHBOARD_URL!,
  },
];
/**
 * Contains the list of regional configurations that are valid; these are really only
 * useful for redirecting a user to a more specific regional endpoint for the app,
 * _not_ to facilitate cross-region request/response.
 */
export const RegionConfig: RegionalConfig[] = regionBaseConfig.filter(
  (region) => Boolean(region.dashboardUrl)
);
